import { useEffect, useState } from 'react'
import { Outlet, Route, Routes, Navigate } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useAuthContext } from './contexts'
import {
  DeviceDetailsPage,
  DevicesPage,
  HomePage,
  LoginPage,
  LogoutPage,
  MapPage,
  PageNotFound,
  ProfilePage,
  SensorPage,
  SettingsPage,
  UserSettingsPage,
  CompleteProfilePage,
} from './pages'
import { AuthCallbackPage } from './pages/AuthCallbackPage'
import { ChartsPage } from './pages/ChartsPage'
import { NavBar } from './components'
import './App.css'
import { CreateAccountPage } from './pages/CreateAccountPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { RegisterPage } from './pages/RegisterPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { UserPreferences } from './pages/UserPreferences'
import { ContactPage } from './pages/ContactPage'
import { InfoPage } from './pages/InfoPage'
import './index.css'
import { ManageDataPage } from './pages/ManageDataPage'
import { NewMainLayout } from './layouts/NewMainLayout'
import { FEATURE_FLAGS } from './utils/featureFlags'
import MessagesPage from './pages/MessagesPage'
import ReportsListPage from './pages/reports/ReportsListPage'
import ReportsIndex from './pages/reports/ReportsIndex'
import ReportsLayout from './pages/reports/layout/ReportsLayout'
import ReportSettingsPage from './pages/reports/ReportSettingsPage'
import ReportViewPage from './pages/reports/ReportViewPage'
import FlowBillingPage from './pages/admin/reports/FlowBillingPage'
import AlertsPage from './pages/AlertsPage'
import DashboardPage from './pages/DashboardPage'
import AdminProtectedRoute from './components/AdminProtectedRoute'
import AdminLayout from './pages/admin/layout/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import SystemHealthPage from './pages/admin/SystemHealthPage'
import MissingReadingsDayPage from './pages/admin/missingReadings/MissingReadingsDayPage'
import DeviceMissingReadingsDayPage from './pages/admin/missingReadings/DeviceMissingReadingsDayPage'
import AlertsLayout from './pages/alerts/layout/AlertsLayout'
import AlertHistoryLogPage from './pages/alerts/AlertHistoryLogPage'
import DirectEmailTestPage from './pages/alerts/test/DirectEmailTestPage'
import DirectSmsTestPage from './pages/alerts/test/DirectSmsTestPage'
import PipelineEmailTestPage from './pages/alerts/test/PipelineEmailTestPage'
import PipelineSmsTestPage from './pages/alerts/test/PipelineSmsTestPage'
import RecipientsPage from './pages/alerts/recipients/RecipientsPage'
import CatchAllSettingsPage from './pages/alerts/settings/SettingsPage'
import FeatureFlagsPage from './pages/alerts/flags/FeatureFlagsPage'
import AlertSubscriptionsPage from './pages/alerts/subscriptions/SubscriptionsPage'
import DeviceMutesPage from './pages/alerts/mutes/DeviceMutesPage'
import DeviceList from './pages/admin/devices/DeviceList'
import DeviceEdit from './pages/admin/devices/DeviceEdit'
import GroupList from './pages/admin/groups/GroupList'
import GroupEdit from './pages/admin/groups/GroupEdit'
import GroupCreate from './pages/admin/groups/GroupCreate'
import UserList from './pages/admin/users/UserList'
import UserEdit from './pages/admin/users/UserEdit'
import UnitCategoryList from './pages/admin/units/UnitCategoryList'
import UnitCategoryEdit from './pages/admin/units/UnitCategoryEdit'
import UnitCategoryCreate from './pages/admin/units/UnitCategoryCreate'
import GuestDeviceListPage from './pages/GuestDeviceListPage'
import PartnerProtectedRoute from './components/PartnerProtectedRoute'
import PartnerLayout from './pages/partner/layout/PartnerLayout'
import PartnerDashboard from './pages/partner/PartnerDashboard'
import CustomerList from './pages/partner/CustomerList'
import CustomerInvite from './pages/partner/CustomerInvite'
import CustomerDetail from './pages/partner/CustomerDetail'
import PendingCustomerDetail from './pages/partner/PendingCustomerDetail'
import MyDevices from './pages/partner/MyDevices'
import ApiDocs from './pages/partner/ApiDocs'
import ApiKeyList from './pages/admin/apiKeys/ApiKeyList'
import { api } from './services'

// Public routes shared between the new-layout and legacy-layout route trees.
// Declared once so the two trees can't drift — a public route present in only
// one tree would be redirected to /login (or 404'd) in the other.
// Keep aligned with src/config/publicRoutes.ts.
const publicRoutes = (
  <>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/logout" element={<LogoutPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/registration/complete" element={<CompleteProfilePage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/auth/callback" element={<AuthCallbackPage />} />
    <Route path="/create-account" element={<CreateAccountPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/ricster/terms-of-service" element={<InfoPage name="ricster/terms-of-service" />} />
    <Route path="/ricster/privacy-policy" element={<InfoPage name="ricster/privacy-policy" />} />
    <Route path="/flowster/terms-of-service" element={<InfoPage name="flowster/terms-of-service" />} />
    <Route path="/flowster/privacy-policy" element={<InfoPage name="flowster/privacy-policy" />} />
    <Route path="/guest/devices" element={<GuestDeviceListPage />} />
  </>
)

function App() {
  const { isAuthorized, email } = useAuthContext()
  const [apiGitHash, setApiGitHash] = useState<string>('')

  // Update document title with email when logged in
  useEffect(() => {
    document.title = email ? `IoAg | ${email}` : 'IoAg'
  }, [email])

  // Fetch API version on mount
  useEffect(() => {
    api.getVersion()
      .then(data => setApiGitHash(data.gitHash))
      .catch(() => {}) // Silently fail - webapp hash will display alone
  }, [])

  const commitHash = process.env.REACT_APP_GIT_COMMIT

  // Use new layout if NEW_LAYOUT flag is enabled
  if (FEATURE_FLAGS.NEW_LAYOUT) {
    return (
      <>
        <NewMainLayout showSidebar={FEATURE_FLAGS.DEVICE_SIDEBAR}>
        <Routes>
          {/* Public routes */}
          <Route
            index
            element={isAuthorized === undefined ? null : isAuthorized ? <Navigate to="/map" replace /> : <LoginPage />}
          />
          {publicRoutes}

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/map" element={<MapPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/details" element={<DeviceDetailsPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:deviceId" element={<DeviceDetailsPage />} />
            <Route path="/devices/:deviceId/:sensorId" element={<SensorPage />} />
            <Route path="/home" element={<Navigate to="/map" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
            <Route path="/user-settings" element={<UserSettingsPage />} />
            <Route path="/user-preferences" element={<UserPreferences />} />
            <Route path="/preferences" element={<Navigate to="/settings/preferences" replace />} />
            <Route path="/manage-data" element={<ManageDataPage />} />
            <Route path="/charts" element={<ChartsPage />} />
            <Route path="/compare" element={<ChartsPage />} />
            {/* The Report Panel wraps every /reports path. Static children are
                declared before :runId — React Router ranks them higher anyway,
                but the ordering is the documentation. */}
            <Route path="/reports" element={<ReportsLayout />}>
              {/* Admins land on Generation, districts on the list. */}
              <Route index element={<ReportsIndex />} />
              <Route path="flowmeter" element={<ReportsListPage />} />
              <Route path="settings" element={<ReportSettingsPage />} />
              <Route element={<AdminProtectedRoute />}>
                <Route path="flowmeter/generation" element={<FlowBillingPage />} />
              </Route>
              <Route path=":runId" element={<ReportViewPage />} />
            </Route>
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/raw" element={<MessagesPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/sensors" element={<SensorPage />} />
            <Route path="/maps" element={<Navigate to="/map" replace />} />
          </Route>
          {FEATURE_FLAGS.ADMIN_SECTION && (
            <Route element={<AdminProtectedRoute />}>
              <Route path="/system-health" element={<SystemHealthPage />} />
              <Route path="/system-health/:tab" element={<SystemHealthPage />} />
              {/* Missing-readings drill-in. Top-level rather than under
                  /admin on purpose: it is reached from the dashboard cards,
                  and dropping the reader into the admin sidebar mid-diagnosis
                  is a context switch they did not ask for. Same reasoning as
                  /system-health above. The fleet day is declared first — it is
                  the parent of the per-device view, not a sibling. */}
              <Route path="/missing-readings/:date" element={<MissingReadingsDayPage />} />
              <Route
                path="/missing-readings/:date/:deviceId"
                element={<DeviceMissingReadingsDayPage />}
              />
              <Route path="/admin-alerts" element={<AlertsLayout />}>
                <Route index element={<Navigate to="log" replace />} />
                <Route path="log" element={<AlertHistoryLogPage />} />
                <Route path="recipients" element={<RecipientsPage />} />
                <Route path="settings" element={<CatchAllSettingsPage />} />
                <Route path="phases" element={<FeatureFlagsPage />} />
                <Route path="subscriptions" element={<AlertSubscriptionsPage />} />
                <Route path="mutes" element={<DeviceMutesPage />} />
                <Route path="test/direct-email" element={<DirectEmailTestPage />} />
                <Route path="test/direct-sms" element={<DirectSmsTestPage />} />
                <Route path="test/pipeline-email" element={<PipelineEmailTestPage />} />
                <Route path="test/pipeline-sms" element={<PipelineSmsTestPage />} />
              </Route>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="system-health" element={<Navigate to="/system-health" replace />} />
                <Route path="devices" element={<DeviceList />} />
                <Route path="devices/:id/edit" element={<DeviceEdit />} />
                <Route path="groups" element={<GroupList />} />
                <Route path="groups/new" element={<GroupCreate />} />
                <Route path="groups/:id/edit" element={<GroupEdit />} />
                <Route path="groups/:id/edit/:tab" element={<GroupEdit />} />
                <Route path="users" element={<UserList />} />
                <Route path="users/:id/edit" element={<UserEdit />} />
                <Route path="users/:id/edit/:tab" element={<UserEdit />} />
                <Route path="units" element={<UnitCategoryList />} />
                <Route path="units/new" element={<UnitCategoryCreate />} />
                <Route path="units/:id/edit" element={<UnitCategoryEdit />} />
                <Route path="api-keys" element={<ApiKeyList />} />
              <Route
                path="reports/flow-billing"
                element={<Navigate to="/reports/flowmeter/generation" replace />}
              />
              </Route>
            </Route>
          )}
          <Route element={<PartnerProtectedRoute />}>
            <Route path="/partner" element={<PartnerLayout />}>
              <Route index element={<PartnerDashboard />} />
              <Route path="customers" element={<CustomerList />} />
              <Route path="customers/invite" element={<CustomerInvite />} />
              <Route path="customers/pending/:pendingAccountId" element={<PendingCustomerDetail />} />
              <Route path="customers/:userId" element={<CustomerDetail />} />
              <Route path="devices" element={<MyDevices />} />
              <Route path="api-docs" element={<ApiDocs />} />
              <Route path="api-keys" element={<Navigate to="/settings/api-keys" replace />} />
            </Route>
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </NewMainLayout>
      <div className="fixed bottom-1 right-2 text-xs text-gray-400 select-none pointer-events-none z-50">
        {commitHash}{apiGitHash && ` | ${apiGitHash}`}
      </div>
      </>
    )
  }

  // Fallback to old layout when flags are disabled
  return (
    <>
    <div>
      <NavBar />
      <Outlet />
      <Routes>
        {/* Public routes */}
        <Route index element={isAuthorized === undefined ? null : isAuthorized ? <HomePage /> : <LoginPage />} />
        {publicRoutes}

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/details" element={<DeviceDetailsPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/devices/:deviceId" element={<DeviceDetailsPage />} />
          <Route path="/devices/:deviceId/:sensorId" element={<SensorPage />} />
          <Route path="/home" element={<MapPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/:tab" element={<SettingsPage />} />
          <Route path="/user-settings" element={<UserSettingsPage />} />
          <Route path="/user-preferences" element={<UserPreferences />} />
          <Route path="/preferences" element={<Navigate to="/settings/preferences" replace />} />
          <Route path="/manage-data" element={<ManageDataPage />} />
          <Route path="/charts" element={<ChartsPage />} />
          <Route path="/compare" element={<ChartsPage />} />
          <Route path="/reports" element={<ReportsLayout />}>
            <Route index element={<Navigate to="/reports/flowmeter" replace />} />
            <Route path="flowmeter" element={<ReportsListPage />} />
            <Route path="settings" element={<ReportSettingsPage />} />
            <Route path=":runId" element={<ReportViewPage />} />
          </Route>
            <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/raw" element={<MessagesPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/sensors" element={<SensorPage />} />
          <Route path="/maps" element={<MapPage />} />
        </Route>

        <Route element={<PartnerProtectedRoute />}>
          <Route path="/partner" element={<PartnerLayout />}>
            <Route index element={<PartnerDashboard />} />
            <Route path="customers" element={<CustomerList />} />
            <Route path="customers/invite" element={<CustomerInvite />} />
            <Route path="customers/:userId" element={<CustomerDetail />} />
            <Route path="devices" element={<MyDevices />} />
            <Route path="api-docs" element={<ApiDocs />} />
            <Route path="api-keys" element={<Navigate to="/settings/api-keys" replace />} />
          </Route>
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </div>
    <div className="fixed bottom-1 right-2 text-xs text-gray-400 select-none pointer-events-none z-50">
      {commitHash}{apiGitHash && ` | ${apiGitHash}`}
    </div>
    </>
  )
}

export default App
